import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
  AttachMoney,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface BudgetTrackerWidgetProps {
  widgetId: string;
  title: string;
}

interface VarianceData {
  department: string;
  varianceToday: number;
  varianceToDate: number;
  category: string;
  isPositive: boolean;
  displayValue: string;
  displaySign: string;
}

interface CostDetailData {
  category: string;
  subCategory: string;
  totalBudget: number;
  totalUsed: number;
  dailyTotal: number;
  percentUsed: number;
  displayBudget: string;
  displayUsed: string;
}

interface BudgetData {
  hasData: boolean;
  message?: string;
  currentDay?: number;
  episodeName?: string;
  location?: string;
  reportDate?: string;
  budget?: {
    episodicBudget: number;
    estimatedFinalCost: number;
    cumulativeVariance: number;
    status: string;
    statusColor: string;
    displayVariance: string;
    varianceSign: string;
  };
  variances?: VarianceData[];
  costDetails?: CostDetailData[];
  trend?: Array<{ day: number; variance: number; date: string | null }>;
  dataQuality?: {
    score: number;
    method: string;
  };
}

const BudgetTrackerWidget: React.FC<BudgetTrackerWidgetProps> = ({
  widgetId,
  title,
}) => {
  const [data, setData] = useState<BudgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getBudgetSummaryData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load budget data');
      }
    } catch (err) {
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'under_budget':
        return <CheckCircle sx={{ color: '#48BB78', fontSize: 28 }} />;
      case 'on_budget':
        return <CheckCircle sx={{ color: '#4299E1', fontSize: 28 }} />;
      case 'slightly_over':
        return <Warning sx={{ color: '#ED8936', fontSize: 28 }} />;
      case 'over_budget':
        return <Warning sx={{ color: '#F56565', fontSize: 28 }} />;
      default:
        return <AccountBalanceWallet sx={{ color: '#4299E1', fontSize: 28 }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'under_budget':
        return 'Under Budget';
      case 'on_budget':
        return 'On Budget';
      case 'slightly_over':
        return 'Slightly Over';
      case 'over_budget':
        return 'Over Budget';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title={title} />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={title}
          action={
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchData} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data?.hasData) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={title}
          action={
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchData} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Box textAlign="center">
            <AccountBalanceWallet sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {data?.message || 'No Hot Cost data available yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Budget data will appear here once Hot Cost reports are processed.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const budget = data.budget!;
  const variances = data.variances || [];
  const costDetails = data.costDetails || [];

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={title}
        subheader={`Day ${data.currentDay} • ${data.episodeName}`}
        action={
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchData} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />

      <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Budget Status Hero Card */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: budget.statusColor + '15',
            border: `2px solid ${budget.statusColor}`,
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              {getStatusIcon(budget.status)}
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: budget.statusColor }}>
                  {budget.varianceSign}{budget.displayVariance}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cumulative Variance
                </Typography>
              </Box>
            </Box>
            <Chip
              label={getStatusLabel(budget.status)}
              sx={{
                backgroundColor: budget.statusColor,
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          </Box>
        </Paper>

        {/* Location */}
        {data.location && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            📍 {data.location}
          </Typography>
        )}

        {/* Department Variances */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Department Variances
        </Typography>
        <Box sx={{ mb: 3 }}>
          {variances.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No variance data available
            </Typography>
          ) : (
            variances.map((v, index) => (
              <Paper
                key={index}
                sx={{
                  p: 1.5,
                  mb: 1,
                  backgroundColor: '#2D3748',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {v.varianceToDate < 0 ? (
                    <TrendingDown sx={{ color: '#48BB78', fontSize: 20 }} />
                  ) : (
                    <TrendingUp sx={{ color: '#F56565', fontSize: 20 }} />
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {v.department}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {v.category}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ color: v.varianceToDate < 0 ? '#48BB78' : '#F56565' }}
                >
                  {v.displaySign}{v.displayValue}
                </Typography>
              </Paper>
            ))
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Cost Breakdown */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Cost Breakdown
        </Typography>
        <Box>
          {costDetails.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No cost breakdown available
            </Typography>
          ) : (
            costDetails.map((detail, index) => (
              <Paper
                key={index}
                sx={{
                  p: 1.5,
                  mb: 1,
                  backgroundColor: '#2D3748',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    {detail.category} / {detail.subCategory}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detail.displayUsed} of {detail.displayBudget}
                  </Typography>
                </Box>
                {detail.totalBudget > 0 && (
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(detail.percentUsed, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#4A5568',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: detail.percentUsed > 90 ? '#F56565' : detail.percentUsed > 75 ? '#ED8936' : '#48BB78',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {detail.percentUsed.toFixed(1)}% used
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))
          )}
        </Box>

        {/* Data Quality Indicator */}
        {data.dataQuality && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #4A5568' }}>
            <Typography variant="caption" color="text.secondary">
              Data Quality: {(data.dataQuality.score * 100).toFixed(0)}% • {data.dataQuality.method}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BudgetTrackerWidget;
